import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";

/**
 * Shared "confirm your email" panel — iteration 21. Renders the 6-digit code
 * input, verify button, and resend button with a live 60s countdown. Reused by
 * the player Onboarding and the StreamerOnboarding email-locked states.
 *
 * `testIdPrefix` distinguishes the two hosts (ob / streamer-onb) so existing
 * data-testids stay descriptive.
 */
export default function EmailCodePanel({ testIdPrefix = "ob", onConfirmed }) {
  const { refresh } = useAuth();
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  const startCooldown = useCallback((seconds) => {
    setCooldown(Math.max(0, Math.floor(seconds || 0)));
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const onCodeChange = (e) => {
    // Strip any non-digit, cap at 6. Preserves leading zeros as string.
    const cleaned = (e.target.value || "").replace(/\D+/g, "").slice(0, 6);
    setCode(cleaned);
  };

  const verify = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await api.post("/auth/verify-email-code", { code });
      toast.success(t("emailCode.success"));
      await refresh();
      if (onConfirmed) onConfirmed();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const c = (d && typeof d === "object" && d.code) || null;
      const key = c ? `emailCode.error.${c}` : "emailCode.error.invalid";
      const msg = t(key);
      toast.error(msg && msg !== key ? msg : apiErrorMessage(e, t("emailCode.error.invalid")));
      // On lock/expired the code is dead server-side too — clear the input.
      if (c === "locked" || c === "expired") setCode("");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setResendBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(t("emailCode.resent"));
      setCode("");
      startCooldown(data?.cooldownSeconds || 60);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "cooldown") {
        startCooldown(d.retryInSeconds || 60);
      }
      toast.error(apiErrorMessage(e, t("emailCode.error.resend")));
    } finally { setResendBusy(false); }
  };

  return (
    <div className="border border-[#F5B841]/40 bg-[#F5B841]/5 p-8 text-center" data-testid={`${testIdPrefix}-email-code-panel`}>
      <div className="font-mono text-[11px] uppercase tracking-widest text-[#F5B841] mb-4">{t("emailCode.eyebrow")}</div>
      <h1 className="font-display text-3xl font-black tracking-tighter mb-3">{t("emailCode.title")}</h1>
      <p className="text-sm text-[#8B97AE] mb-6 max-w-md mx-auto">{t("emailCode.body")}</p>
      <input
        data-testid={`${testIdPrefix}-code-input`}
        type="text"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        autoComplete="one-time-code"
        value={code}
        onChange={onCodeChange}
        onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6 && !busy) verify(); }}
        placeholder="000000"
        className="mx-auto block w-56 text-center rounded-none bg-transparent border border-white/20 focus:border-[#F5B841] focus:outline-none h-14 text-3xl font-mono tracking-[0.5em] text-[#E8ECF4] py-2"
        style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      />
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <Button
          data-testid={`${testIdPrefix}-verify-btn`}
          onClick={verify}
          disabled={code.length !== 6 || busy}
          className="rounded-none bg-[#F5B841] text-[#04150F] hover:bg-white h-11 px-6 font-semibold transition-colors disabled:opacity-40"
        >
          {busy ? "…" : t("emailCode.verify")}
        </Button>
        <Button
          data-testid={`${testIdPrefix}-resend-btn`}
          onClick={resend}
          disabled={cooldown > 0 || resendBusy}
          variant="ghost"
          className="rounded-none border border-white/15 h-11 px-5 font-semibold text-[#E8ECF4] hover:bg-white/10 transition-colors disabled:opacity-40"
        >
          {cooldown > 0 ? `${t("emailCode.resendIn")} ${cooldown}s` : t("emailCode.resend")}
        </Button>
      </div>
    </div>
  );
}
